<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { CenteredContactProps } from "./contact.interfaces";
import { computed, ref } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import FormField from "./form-field/FormField.vue";
import SubmitButton from "./submit-button/SubmitButton.vue";
import PrivacyPolicy from "./privacy-policy/PrivacyPolicy.vue";

const props = defineProps<{
    block: PageBlock<CenteredContactProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const agreed = ref(dataValues.value.privacyPolicy.isHidden);
const formSubmitted = ref(false);
const isSubmitting = ref(false);
const showSuccessMessage = ref(false);
const showErrorMessage = ref(false);

const handleSubmit = (e: Event) => {
    e.preventDefault();
    formSubmitted.value = true;

    if (!agreed.value) {
        return;
    }

    isSubmitting.value = true;
    const url = dataValues.value.submitButton.data.hookUrl;
    const formData = Object.fromEntries(new FormData(e.target as HTMLFormElement));

    fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
    })
        .then(() => {
            isSubmitting.value = false;
            showSuccessMessage.value = true;
        })
        .catch(() => {
            isSubmitting.value = false;
            showErrorMessage.value = true;
        });
};
</script>

<template>
    <Section :id="block.webId" class="relative isolate" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="mx-auto max-w-2xl text-center">
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <Heading
                        v-bind="dataValues.title.data"
                        :textColor="styleValues.text"
                        class="text-4xl sm:text-5xl"
                    />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    field-name="description"
                >
                    <Paragraph
                        v-bind="dataValues.description.data"
                        :textColor="styleValues.text"
                        class="mt-6 text-lg/8 opacity-70"
                    />
                </EditableElement>
            </div>
            <form class="mx-auto mt-16 max-w-xl sm:mt-20" @submit="handleSubmit">
                <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                    <EditableElement
                        v-for="field in dataValues.fields.data.items"
                        :key="field.id"
                        :blockId="block.id"
                        :field-name="`fields_${field.id}`"
                        :hideable="false"
                    >
                        <FormField
                            v-bind="field"
                            :label-color="styleValues.text"
                            :accent-color="styleValues.brand"
                            :border-color="styleValues.border"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.privacyPolicy.isHidden"
                        :blockId="block.id"
                        field-name="privacyPolicy"
                    >
                        <PrivacyPolicy
                            v-bind="dataValues.privacyPolicy.data"
                            :accent-color="styleValues.brand"
                            v-model:agreed="agreed"
                        />
                    </EditableElement>
                </div>
                <div class="mt-10 space-y-4">
                    <EditableElement
                        v-if="!dataValues.submitButton.isHidden"
                        :blockId="block.id"
                        field-name="submitButton"
                    >
                        <SubmitButton
                            :text="dataValues.submitButton.data.label"
                            :text-color="styleValues.background"
                            :background-color="styleValues.brand"
                            :is-submitting="isSubmitting"
                        />
                    </EditableElement>
                </div>
                <p v-if="formSubmitted && !agreed" class="mt-4 text-sm text-red-600 text-center">
                    {{ dataValues.privacyPolicy.data.validationMessage }}
                </p>
                <p v-if="showSuccessMessage" class="mt-4 text-sm text-green-600 text-center">
                    {{ dataValues.submitButton.data.successMessage }}
                </p>
                <p v-if="showErrorMessage" class="mt-4 text-sm text-red-600 text-center">
                    {{ dataValues.submitButton.data.errorMessage }}
                </p>
            </form>
        </FullSpacing>
    </Section>
</template>
